import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {Alert, Box, Button, Checkbox, Container, FormControlLabel, Link, Stack, Typography} from "@mui/material";
import WarningIcon from "@mui/icons-material/ReportGmailerrorred";
import {OrderMap} from "../orders/OrderMap";
import {Order} from "../../types/domain/order";
import {useOrderManagement} from "../../hooks/ordersHooks";
import {OrderStatus} from "../../types/domain/orderStatus";
import {swallowException} from "../../utils/appUtils";
import {KeyValueInline} from "../base/KeyValueInline";
import {AppLayout} from "../AppLayout";
import {getOrderPeriod} from "../../utils/orderUtils";
import {createFeatureCollection} from "../../utils/mapUtils";
import {ActionButton} from "../base/ActionButton";
import {useFields} from "../../hooks/fieldsHooks";
import {useOrderById} from "../../hooks/orderById";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {getResourceErrorMessage, isResourceFailed} from "../../utils/resourceUtils";
import {ErrorAlert} from "../base/ErrorAlert";
import {useNavigate} from "react-router-dom";

export const OrderConfirmationPage: React.FC = () => {
    const {t} = useTranslation();
    const {dateFormat} = useUserPreferences();
    const msUtils = useMeasureSystemUtils();
    const [cancelConfirm, setCancelConfirm] = useState(false);
    const orderManagement = useOrderManagement();
    const params = useParams<{companyId: string; orderId: string}>();
    const companyId = params.companyId!;
    const orderId = params.orderId!;
    const orderState = useOrderById({companyId, orderId});
    const [order, setOrder] = useState<Order | null>(null);
    const [isAgreementChecked, setIsAgreementChecked] = useState(false);
    const [isPriceConfirmationChecked, setIsPriceConfirmationChecked] = useState(false);
    const fieldsState = useFields({companyId, orderId: orderId, farmId: undefined});
    const fields = fieldsState.isSuccess ? fieldsState.data : [];
    const hasDiscount = order?.discount !== 0;
    const navigate = useNavigate();

    useEffect(() => {
        if (orderState.isLoading === false) {
            setOrder(orderState.item);
        } else {
            setOrder(null);
        }
    }, [orderState.isLoading, orderState.item]);

    const isSubmitDisabled = () => {
        return (
            !order ||
            order.state !== OrderStatus.AWAITING_CONFIRMATION ||
            !isAgreementChecked ||
            !isPriceConfirmationChecked
        );
    };

    const onSubmit = () => {
        orderManagement
            .changeStatus(companyId, orderId, OrderStatus.ACTIVE)
            .then(() => {
                orderManagement.reset();
                setOrder({...order!, state: OrderStatus.ACTIVE});
                navigate({
                    pathname: `/organization/${companyId}/orders`,
                    search: location.search,
                });
            })
            .catch(swallowException);
    };

    const onCancel = () => {
        orderManagement
            .changeStatus(companyId, orderId, OrderStatus.CANCELED)
            .then(() => {
                orderManagement.reset();
                setOrder({...order!, state: OrderStatus.CANCELED});
            })
            .catch(swallowException);
        setCancelConfirm(false);
    };

    const orderArea = (order: Order): string => {
        const area = order.areaInHa ? msUtils.getArea(order.areaInHa) : 0;
        return `${area.toFixed(2)} ${msUtils.getAreaUnits()}`;
    };

    return (
        <AppLayout>
            {isResourceFailed(orderManagement.resource) && (
                <ErrorAlert
                    message={getResourceErrorMessage(orderManagement.resource)!}
                    onClose={() => orderManagement.reset()}
                />
            )}
            {order === null && !orderState.isLoading && (
                <Container>
                    <Typography component="h3" variant="h4" gutterBottom>
                        {t("order_confirmation_page.no_order_id", {orderId})}
                    </Typography>
                </Container>
            )}
            {order && order.state === OrderStatus.NEW && (
                <Alert severity="warning">{t("order_confirmation_page.review")}</Alert>
            )}
            {order && (
                <Box display="flex" justifyContent="center" alignItems="flex-start" p={"1.125rem"}>
                    <Stack
                        direction="column"
                        gap="0.5rem"
                        alignItems="flex-start"
                        justifyContent="center"
                        p={"1.125rem"}
                        useFlexGap
                        minWidth="50rem"
                        width="70rem"
                    >
                        <Typography textTransform="uppercase" component="h1" variant="h1">
                            {t("order_confirmation_page.confirm_order_processing")}
                        </Typography>
                        <KeyValueInline header={t("order_confirmation_page.order")} value={order.id}></KeyValueInline>
                        <KeyValueInline
                            header={t("order_confirmation_page.status")}
                            value={order.state}
                        ></KeyValueInline>
                        <KeyValueInline
                            header={t("order_confirmation_page.nb_fields")}
                            value={order.fields.length}
                        ></KeyValueInline>

                        <KeyValueInline
                            header={t("order_confirmation_page.area")}
                            value={orderArea(order)}
                        ></KeyValueInline>
                        <KeyValueInline
                            header={t("order_confirmation_page.order_period")}
                            value={getOrderPeriod(order, dateFormat)}
                        ></KeyValueInline>
                        {hasDiscount && (
                            <>
                                <KeyValueInline
                                    header={t("order_confirmation_page.price")}
                                    value={`EUR ${order.price}`}
                                ></KeyValueInline>
                                <KeyValueInline
                                    header={t("order_confirmation_page.discount")}
                                    value={`${order.discount} %`}
                                ></KeyValueInline>
                            </>
                        )}
                        <KeyValueInline
                            header={t("order_confirmation_page.final_price")}
                            value={`EUR ${order.finalPrice}`}
                        ></KeyValueInline>

                        {fields &&
                            fields.length > 0 &&
                            (() => {
                                const geojson = createFeatureCollection(fields);
                                return <OrderMap geojson={geojson} fields={fields} />;
                            })()}

                        <FormControlLabel
                            label={
                                <Typography>
                                    {t("order_confirmation_page.agreement_text.part1")}{" "}
                                    <Link href="/legal/Terms_of_Service.pdf" target="_blank">
                                        {t("order_confirmation_page.agreement_text.terms_of_service")}
                                    </Link>{" "}
                                    {t("order_confirmation_page.agreement_text.part2")}{" "}
                                    <Link href="/legal/Privacy_Policy.pdf" target="_blank">
                                        {t("order_confirmation_page.agreement_text.privacy_policy")}
                                    </Link>
                                </Typography>
                            }
                            control={
                                <Checkbox
                                    checked={isAgreementChecked}
                                    onChange={(e) => setIsAgreementChecked(e.target.checked)}
                                />
                            }
                        />
                        <FormControlLabel
                            label={
                                <Typography>
                                    {t("order_confirmation_page.agreement_text.price_confirmation", {
                                        price: order.finalPrice,
                                    })}
                                </Typography>
                            }
                            control={
                                <Checkbox
                                    checked={isPriceConfirmationChecked}
                                    onChange={(e) => setIsPriceConfirmationChecked(e.target.checked)}
                                />
                            }
                        />

                        <Stack direction="row" spacing={2}>
                            {!cancelConfirm && (
                                <>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={onSubmit}
                                        disabled={isSubmitDisabled()}
                                    >
                                        {t("order_confirmation_page.comfirm_order")}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setCancelConfirm(true);
                                        }}
                                    >
                                        {t("order_confirmation_page.cancel_order")}
                                    </Button>
                                </>
                            )}
                            {cancelConfirm && (
                                <>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            textTransform: "none",
                                        }}
                                    >
                                        <WarningIcon color="error" />
                                        <Box sx={{ml: "0.75rem"}}>{t("order_confirmation_page.confirm")}</Box>
                                    </div>
                                    <ActionButton
                                        icon="delete"
                                        color="error"
                                        onClick={onCancel}
                                        sx={{marginLeft: "1rem"}}
                                    >
                                        {t("enumerations.boolean.yes")}
                                    </ActionButton>
                                    <ActionButton
                                        icon="back"
                                        color="secondary"
                                        onClick={() => setCancelConfirm(false)}
                                        sx={{ml: "1rem"}}
                                    >
                                        {t("enumerations.boolean.no")}
                                    </ActionButton>
                                </>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            )}
        </AppLayout>
    );
};
